import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CreditProposalResultContext } from "../../contexts/CreditProposalResultContext";
import CreditProposalService from "../../services/CreditProposal";
import { MainButton, Title } from "../../styles/GlobalStyledComponents";
import {
  ButtonSection,
  CardData,
  NewCard,
  OtherCard,
  SelectOtherCard,
  TitleData,
} from "./ExistingPaymentSelectorStyledComponent";

import {} from "../../interfaces/StoredPaymentCards";
import { ROUTES_PATH } from "../../constants";
import { ExistingCardData } from "../../components/ExistingCardData";
import { ReusePaymentCardStatus } from "../../interfaces/ReusePaymentCardRequest";
import { environment } from "../../environments/environment";
import PaymentService from "../../services/Payment/index";
import {
  PaymentState,
  PaymentType,
  areEqualStatuses,
} from "../../interfaces/Payment";
import { ErrorCard } from "../../components/ErrorCard";
import { usePaymentCard } from "../../contexts/PaymentCardContext";
import { DecisionStatus } from "../../interfaces/DecisionEngine";
import AnalyticsService from "../../services/Analytics";
import { DenialStep } from "../../interfaces/Analytics";

interface ExistingPaymentSelectorProps {
  loadingEvent: (data?: any) => void;
}

export const ExistingPaymentSelector: React.FC<
  ExistingPaymentSelectorProps
> = ({ loadingEvent }) => {
  const navigate = useNavigate();
  const [cardExist, setCardExist] = useState<any>();
  const { creditProposalResult } = useContext(CreditProposalResultContext);
  const [selectPayment, setSelectedPayment] = useState<any>(null);
  const [invalidCard, setInvalidCard] = useState<any>(false);
  const { paymentCards, createPaymentCards } = usePaymentCard();

  const handleContinue = () => {
    loadingEvent(true);
    confirmCard();
  };

  const confirmCard = async () => {
    try {
      const reusePayment = await CreditProposalService.postReusePaymentCard(
        creditProposalResult?.credit_proposal_id!,
        selectPayment.payment_id,
        selectPayment.payment_method_id
      );
      switch (reusePayment?.status?.status) {
        case ReusePaymentCardStatus.OK:
          handleOKStatus();
          break;
        case ReusePaymentCardStatus.KO:
          handleKOStatus();
          break;
        case ReusePaymentCardStatus.PND:
          handlePendingStatus(reusePayment.payment_id);
          break;
        default:
          handleKOStatus();
          break;
      }
    } catch (error) {
      handleKOStatus();
    }
  };

  const handleKOStatus = () => {
    const newCardExist = cardExist.filter(
      (paymentCard: any) => paymentCard !== selectPayment
    );
    createPaymentCards(newCardExist);
    setCardExist(newCardExist);
    setInvalidCard(true);
    loadingEvent(false);
  };

  const handleOKStatus = async () => {
    if (creditProposalResult) {
      getPADecision(creditProposalResult?.credit_proposal_id, 0);
    }
  };

  const handlePendingStatus = async (paymentId: string) => {
    let loanCreated = false;
    let currentAtempts = 0;
    const loop = setInterval(async () => {
      currentAtempts++;
      if (
        currentAtempts >= environment.pooling_max_iterations_existing_payment
      ) {
        clearInterval(loop);
        handleKOStatus();
      }
      let paymentStatusObject = await PaymentService.getPaymentStatus(
        creditProposalResult?.credit_proposal_id!,
        paymentId
      );
      const status = paymentStatusObject.status;
      if (
        areEqualStatuses(status, {
          type: PaymentType.REC,
          status: PaymentState.OK,
        }) &&
        !loanCreated
      ) {
        clearInterval(loop);
        navigate(ROUTES_PATH.END);
      }
    }, environment.pooling_interval * 1000);
  };

  const getPADecision = async (creditProposalId: string, counter: number) => {
    loadingEvent(true);
    counter++;
    try {
      const decisionResp = await CreditProposalService.getDecision(
        creditProposalId
      );

      if (decisionResp) {
        loadingEvent(false);
        switch (decisionResp.decision) {
          case DecisionStatus.APPROVED:
          case DecisionStatus.NOT_NECESSARY:
          case DecisionStatus.PENDING:
            navigate(ROUTES_PATH.SIGNATURE);
            break;
          case DecisionStatus.DENIED:
            AnalyticsService.sendLoanRefused({denial_step: DenialStep.PAC});
            navigate(ROUTES_PATH.DENIAL);
            break;
          case DecisionStatus.MISSING_DATA:
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            break;
          default:
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            break;
        }
      }
    } catch (error) {
      if (`${error}`.includes("409") && counter <= 11) {
        setTimeout(() => {
          getPADecision(creditProposalId, counter);
        }, 3000);
      } else {
        loadingEvent(false);
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  const navigateTo = () => {
    navigate(ROUTES_PATH.PAYMENT);
  };

  useEffect(() => {
    loadingEvent(false);
    setCardExist(paymentCards);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {invalidCard ? (
        <ErrorCard
          loadingEvent={false}
          cardExist={cardExist.length}
          buttonClick={(e: boolean) => setInvalidCard(e)}
        />
      ) : (
        <>
          <div>
            <TitleData>
              <Title>¿Dónde deseas realizar el cargo?</Title>
            </TitleData>
            <CardData>
              {cardExist
                ? cardExist.map((card: any) => (
                    <ExistingCardData
                      data={card}
                      key={card.auth_token}
                      selectedPayment={setSelectedPayment}
                      colorSelected={
                        selectPayment?.order === card?.order
                          ? "lightgrey"
                          : "white"
                      }
                    />
                  ))
                : ""}
            </CardData>
            <SelectOtherCard>
              <OtherCard>
                Si lo deseas puedes
                <NewCard onClick={() => navigateTo()}>
                  {" "}
                  indicar otra tarjeta
                </NewCard>
                .
              </OtherCard>
            </SelectOtherCard>
          </div>
          <ButtonSection>
            <MainButton onClick={handleContinue} disabled={!selectPayment}>
              Continuar
            </MainButton>
          </ButtonSection>
        </>
      )}
    </>
  );
};
